import React, { useEffect } from 'react';
import * as ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icons, Row } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { getResponseError, setFormError } from '../../../utils/utils';
import { FORM_VALIDATION_MESSAGE } from '../../../utils/apiError';
import { createVehicles, editVehiclesById } from '../actions';
import Loader from '../../../components/base/Loader/Loader';
import { getVehiclesData } from '../reducer';
import VehiclesDTO from '../../../dto/VehiclesDTO';
import FieldDTOWithCol from '../../../components/fields/Abstract/FieldDTOWithCol';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import CustomSelectInputWithHelper from '../../../components/fields/SelectInput/CustomSelectInputWithHelper';
import {
  PROPULSION_TYPES_LIST,
  VEHICLE_STATE,
  VEHICLE_STATE_LIST,
  VEHICLE_TYPES,
  VEHICLE_ENTITY_TYPES_LIST,
} from '../../../configs';
import { loadProviderList } from '../../Provider/actions';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const toList = value => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return value.split(',').map(item => item.trim()).filter(Boolean);
  }
  return [];
};

const getVehicleDefaults = vehicle => ({
  device_id: vehicle?.device_id || '',
  provider_id: vehicle?.provider_id || '',
  vehicle_id: vehicle?.vehicle_id || '',
  vehicle_type: vehicle?.vehicle_type || VEHICLE_TYPES.SCOOTER,
  propulsion_types: toList(vehicle?.propulsion_types).length ? toList(vehicle?.propulsion_types) : ['electric'],
  year: vehicle?.year || '',
  mfgr: vehicle?.mfgr || '',
  model: vehicle?.model || '',
  state: vehicle?.state || VEHICLE_STATE.UNKNOWN,
  prev_events: toList(vehicle?.prev_events).join(', '),
});

export default function VehiclesForm(props) {
  const {
    onClose = () => {},
    loadData = () => {},
    currentVehicles = null,
  } = props;
  const { loadingCreateVehicles, loadingEditVehiclesById } = useSelector(getVehiclesData);
  const dispatch = useDispatch();
  const {
    reset, control, getValues, setError,
  } = useForm({
    mode: 'onChange',
    defaultValues: getVehicleDefaults(currentVehicles),
  });

  const isEdit = Boolean(currentVehicles?.device_id);

  useEffect(() => {
    reset(getVehicleDefaults(currentVehicles));
  }, [currentVehicles, reset]);

  const showValidationError = (field, message) => {
    setError(field, { type: 'manual', message }, { shouldFocus: true });
    errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
  };

  const buildPayload = () => {
    const values = getValues();
    const vehicleId = values.vehicle_id?.trim();
    const deviceId = values.device_id?.trim();
    const year = values.year === '' || values.year === null || typeof values.year === 'undefined'
      ? null
      : Number(values.year);

    if (deviceId && !UUID_RE.test(deviceId)) {
      showValidationError('device_id', 'Устройство должно быть UUID или пустым');
      return null;
    }
    if (!vehicleId) {
      showValidationError('vehicle_id', 'Введите ИД ТС');
      return null;
    }
    if (!values.provider_id) {
      showValidationError('provider_id', 'Выберите провайдера');
      return null;
    }
    if (!values.vehicle_type) {
      showValidationError('vehicle_type', 'Выберите тип ТС');
      return null;
    }
    if (!Array.isArray(values.propulsion_types) || !values.propulsion_types.length) {
      showValidationError('propulsion_types', 'Выберите тип двигателя');
      return null;
    }
    if (year !== null && (!Number.isInteger(year) || year < 1900 || year > 2100)) {
      showValidationError('year', 'Введите корректный год от 1900 до 2100');
      return null;
    }
    if (!values.state) {
      showValidationError('state', 'Выберите текущее состояние');
      return null;
    }

    const payload = {
      provider_id: values.provider_id,
      vehicle_id: vehicleId,
      vehicle_type: values.vehicle_type,
      propulsion_types: values.propulsion_types,
      mfgr: values.mfgr?.trim() || '',
      model: values.model?.trim() || '',
      state: values.state,
      prev_events: toList(values.prev_events),
    };

    if (deviceId) {
      payload.device_id = deviceId;
    }
    if (year !== null) {
      payload.year = year;
    }

    return payload;
  };

  const handleSave = () => {
    const payload = buildPayload();
    if (!payload) {
      return;
    }

    const action = isEdit
      ? editVehiclesById({ ...payload, device_id: currentVehicles.device_id })
      : createVehicles(payload);

    dispatch(action)
      .then(() => {
        successNotification('Сохранение формы', 'Форма сохранена');
        loadData();
        onClose();
      })
      .catch(response => {
        setFormError(response, setError);
        errorNotification('Сохранение формы', getResponseError(response));
      });
  };

  const dto = new VehiclesDTO();
  const attributes = dto.getAttributesByName();

  return ReactDOM.createPortal(
    <div className="form-dialog vehicles-form">
      <Loader loading={loadingCreateVehicles || loadingEditVehiclesById} />
      <div className="form-header">
        <div className="breadcrumbs">
          <span className="breadcrumbs-prev">Транспортные средства</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-current">ТС № {currentVehicles?.vehicle_id || 'Без номера'}</span>
          <span className="breadcrumbs-current"> ({isEdit ? 'редактирование' : 'создание'}) </span>
        </div>
        <div className="close-button" onClick={onClose}>
          <Icons width={24} height={24} customIcon icon="close" />
        </div>
      </div>
      <div className="form-body">
        <form className="form-content">
          <div className="form-fields">
            <div className="form-fields-wrapper custom-scroll-theme">
              <div className="afc-body">
                <div className="afc-main vehicles-main-info">
                  <Row className="row-cols-1">
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.device_id}
                      variant="material"
                      withLabel
                      textMuted={isEdit ? 'Устройство нельзя менять после создания' : 'Оставьте пустым, чтобы UUID был создан автоматически'}
                      disabled={isEdit}
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.vehicle_id}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      dtoObject={attributes.provider_id}
                      usePortal
                      component={CustomSelectInputWithHelper}
                      portalClassName="executor-selector"
                      loadAction={loadProviderList}
                      optionFormatter={item => ({ value: item.id, name: item.name })}
                      filterName="provider_name"
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      component={CustomSelectInput}
                      dtoObject={attributes.vehicle_type}
                      variant="material"
                      withLabel
                      options={VEHICLE_ENTITY_TYPES_LIST}
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      component={CustomSelectInput}
                      dtoObject={attributes.propulsion_types}
                      variant="material"
                      withLabel
                      multiple
                      options={PROPULSION_TYPES_LIST}
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="number"
                      component={TextInputWithLabel}
                      dtoObject={attributes.year}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.mfgr}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.model}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      component={CustomSelectInput}
                      dtoObject={attributes.state}
                      variant="material"
                      withLabel
                      options={VEHICLE_STATE_LIST}
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.prev_events}
                      variant="material"
                      withLabel
                      textMuted="Несколько событий указывайте через запятую"
                      className="mb-3"
                    />
                  </Row>
                </div>
              </div>
            </div>
          </div>
          <div className="form-footer">
            <Button onClick={() => handleSave()}>{isEdit ? 'Сохранить' : 'Создать'}</Button>
            <Button variant="secondary" onClick={onClose}>Отменить</Button>
          </div>
        </form>
      </div>
    </div>,
    document.getElementById('app-content'),
  );
}
